import { Component, OnInit } from '@angular/core';
import { ModalController, NavParams } from '@ionic/angular';
import {
  ROLEPLAY_INDICACIONES,
  TopicRoleplayDto,
} from 'src/app/pages/evaluacion-practica/models/roleplay.model';
import { RoleplayService } from 'src/app/pages/evaluacion-practica/services/roleplay.service';

/** Qué eligió el usuario al cerrar el modal. */
export type EvaluacionElegida = 'theory' | 'practical';

/**
 * Modal que ramifica cuando un tema trae más de una evaluación.
 *
 * Paso 1: lista las evaluaciones del tema (teórica y/o práctica).
 * Paso 2 (solo práctica): indicaciones previas antes de entrar al roleplay.
 *
 * La teórica no se documenta aquí: al elegirla el modal se cierra devolviendo
 * `theory` y `tema.page` abre el `ModalEvaluacionPage` de siempre, que ya tiene
 * sus propias indicaciones. Así no se duplica esa lógica ni se toca un modal
 * que consumen otras tres páginas.
 */
@Component({
  selector: 'app-modal-seleccion-evaluacion',
  templateUrl: './modal-seleccion-evaluacion.component.html',
  styleUrls: ['./modal-seleccion-evaluacion.component.scss'],
})
export class ModalSeleccionEvaluacionComponent implements OnInit {
  tema: any;
  modEvaluaciones: any;
  /** El bloque `roleplay` del tema, tal cual lo manda backend. */
  roleplay: TopicRoleplayDto | null = null;

  /** Copy de UI: no llega en ningún payload. */
  readonly indicaciones = ROLEPLAY_INDICACIONES;

  /** 'lista' | 'indicaciones-practica' */
  paso: 'lista' | 'indicaciones-practica' = 'lista';

  constructor(
    private modalCtrl: ModalController,
    private navParams: NavParams,
    private roleplayService: RoleplayService
  ) {}

  ngOnInit(): void {
    this.tema = this.navParams.get('tema');
    this.modEvaluaciones = this.navParams.get('modEvaluaciones');
    this.roleplay = this.navParams.get('roleplay') ?? null;

    // Si el tema solo trae la práctica, no tiene sentido hacer elegir: se
    // entra directo a sus indicaciones. Salvo que esté bloqueada, porque el
    // salto llevaría al botón de empezar una evaluación que no se puede dar.
    if (!this.tieneTeorica && this.roleplay && !this.practicaBloqueada) {
      this.paso = 'indicaciones-practica';
    }
  }

  // ── teórica ────────────────────────────────────────────────────────────────

  get tieneTeorica(): boolean {
    return this.tema?.evaluable === 'si';
  }

  get teoricaCompletada(): boolean {
    if (!this.tieneTeorica) return false;
    const notaAprobatoria =
      +this.tema?.mod_evaluaciones?.nota_aprobatoria ||
      +this.modEvaluaciones?.nota_aprobatoria;

    if (this.tema?.tipo_ev === 'open') {
      return this.tema?.estado_tema === 'realizado';
    }
    return +this.tema?.nota >= notaAprobatoria;
  }

  /**
   * No se puede elegir la teórica. Replica las mismas condiciones que los
   * `[disabled]` de los botones del tema:
   *
   * - la `open` no se repite una vez realizada;
   * - las demás necesitan intentos (`> 0` cubre null/undefined además del 0).
   *
   * Ojo: una `qualified` **aprobada pero con intentos** sigue elegible — es el
   * "Mejora tu nota" de siempre.
   */
  get teoricaBloqueada(): boolean {
    if (!this.tieneTeorica) return true;
    if (this.tema?.tipo_ev === 'open' && this.tema?.estado_tema === 'realizado')
      return true;
    return !(this.tema?.intentos_restantes > 0);
  }

  /** Chip de estado de la teórica. */
  get teoricaChip(): string {
    if (this.teoricaCompletada) return 'Realizada';
    return this.teoricaBloqueada ? 'Sin intentos' : 'Pendiente';
  }

  /** Título de la card, según el subtipo. */
  get teoricaTitulo(): string {
    if (this.tema?.tipo_ev === 'open') {
      return this.tema?.open_evaluation_button || 'Evaluación';
    }
    if (this.tema?.tipo_ev === 'scorm-evaluation') {
      return 'Evaluación dentro del contenido';
    }
    return 'Evaluación teórica';
  }

  /**
   * La `scorm-evaluation` no abre un modal de preguntas: abre el propio
   * contenido a pantalla completa, y conviene avisarlo antes del click.
   */
  get teoricaEsScorm(): boolean {
    return this.tema?.tipo_ev === 'scorm-evaluation';
  }

  // ── práctica ───────────────────────────────────────────────────────────────

  /** Ya la rindió: gastó al menos un intento (el tema no trae un estado). */
  get practicaCompletada(): boolean {
    return this.roleplayService.isDone(this.roleplay);
  }

  /**
   * Ya aprobada y el espacio no permite repetirla
   * (`allow_practice_after_pass: false`), aunque le queden intentos.
   */
  get practicaCerradaPorAprobacion(): boolean {
    return this.roleplayService.cerradoPorAprobacion(this.roleplay);
  }

  /**
   * No se puede elegir la práctica: sin intentos, ya aprobada sin permiso de
   * repetirla, o en una modalidad que esta versión de la app no sabe correr (si
   * se dejara clickeable, el modal se cerraría y no pasaría nada).
   */
  get practicaBloqueada(): boolean {
    if (!this.roleplay) return true;
    if (!this.roleplayService.modoSoportado(this.roleplay)) return true;
    // Sin intentos no se entra, esté realizada o no. Simétrico con la teórica:
    // "realizada con intentos" sí es elegible (equivale a "Mejora tu nota"),
    // salvo que ya esté aprobada y el espacio cierre la repetición.
    return !this.roleplayService.puedeIntentar(this.roleplay);
  }

  /** Chip de estado de la práctica. */
  get practicaChip(): string {
    if (this.practicaCerradaPorAprobacion) return 'Aprobada';
    if (this.practicaCompletada) return 'Realizada';
    return this.roleplayService.hasAttemptsLeft(this.roleplay)
      ? 'Pendiente'
      : 'Sin intentos';
  }

  /** La modalidad configurada no existe en esta versión: hay que decirlo. */
  get modalidadNoSoportada(): boolean {
    return !!this.roleplay && !this.roleplayService.modoSoportado(this.roleplay);
  }

  /** Intentos que le quedan: `max_attempts - attempts`. */
  get intentosRestantes(): number {
    return this.roleplayService.attemptsLeft(this.roleplay);
  }

  /** La práctica genera nota; si no, es solo un paso a completar. */
  get practicaEvaluable(): boolean {
    return !!this.roleplay?.is_evaluable;
  }

  /**
   * Cómo se combina con la teórica. Solo tiene sentido decirlo si hay teórica
   * con la que combinarse.
   */
  get notaCombinadaLabel(): string {
    if (!this.tieneTeorica || !this.practicaEvaluable) return '';
    return this.roleplay?.grade_mode === 'averaged_with_exam'
      ? 'Tu resultado se promedia con el de la evaluación teórica.'
      : '';
  }

  get modalidadLabel(): string {
    switch (this.roleplay?.student_mode) {
      case 'voice':
        return 'VOZ';
      case 'video':
        return 'VIDEO';
      case 'streaming':
        return 'STREAM';
      default:
        return 'CHAT';
    }
  }

  /** Línea extra de indicaciones propia de cada modalidad. */
  get indicacionModalidad(): string {
    switch (this.roleplay?.student_mode) {
      case 'voice':
        return 'Necesitarás permiso de micrófono para poder responder.';
      case 'video':
        return 'Necesitarás permiso de cámara y micrófono para poder responder.';
      case 'streaming':
        return 'Vas a conversar en vivo por voz: necesitarás micrófono y un lugar silencioso.';
      default:
        return '';
    }
  }

  // ── acciones ───────────────────────────────────────────────────────────────

  elegirTeorica(): void {
    if (this.teoricaBloqueada) return;
    this.modalCtrl.dismiss({ choice: 'theory' as EvaluacionElegida });
  }

  elegirPractica(): void {
    if (this.practicaBloqueada) return;
    this.paso = 'indicaciones-practica';
  }

  empezarPractica(): void {
    if (this.practicaBloqueada) return;
    this.modalCtrl.dismiss({ choice: 'practical' as EvaluacionElegida });
  }

  volverALista(): void {
    this.paso = 'lista';
  }

  /** Solo se puede volver a la lista si además hay teórica que elegir. */
  get puedeVolver(): boolean {
    return this.tieneTeorica;
  }

  cerrar(): void {
    this.modalCtrl.dismiss();
  }
}
